import React, { useState, useCallback, useMemo } from 'react';
import { DndContext, closestCorners } from '@dnd-kit/core';
import TaskBoardContent from './TaskBoardContent';
import { Task } from '@/types/tasks';
import { ViewMode } from './TaskViewContainer';
import { useTaskBoardState } from './hooks/useTaskBoardState';
import { useTaskBoardDrag } from './hooks/useTaskBoardDrag';
import { useTaskManagerBoardDrag } from './hooks/task-drag/use-task-manager-board-drag';
import { useTaskSensors } from './hooks/useTaskSensors';
import { useDragContext } from '@/contexts/drag/DragContext';
import TaskBoardLoading from './components/TaskBoardLoading';
import TaskAddHandler from './components/TaskAddHandler';

// Re-export sample tasks from the separate file
export { sampleTasks } from './sample-tasks';

interface TaskBoardProps {
  onAddTask?: (taskData: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void;
  initialTasks?: Task[];
  viewMode?: ViewMode;
  currentDate?: Date;
  isTransitioning?: boolean;
  onCreateTask?: () => void;
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  // NEW: TaskManager-specific operations for immediate database persistence
  taskManagerOperations?: {
    updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
    updateTaskOrder?: (taskId: string, newOrder: number) => void;
  };
  // NEW: Project filter props
  selectedProjectId?: string | null;
  onProjectFilterChange?: (projectId: string | null) => void;
  allTasks?: any[];
  projectTaskCounts?: {
    projectCounts: Record<string, number>;
    totalTasks: number;
  };
  // NEW: Task editing props
  editingTaskId?: string | null;
  onCloseEdit?: () => void;
}

const TaskBoard: React.FC<TaskBoardProps> = React.memo(({ 
  onAddTask,
  initialTasks,
  viewMode = 'week',
  currentDate = new Date(),
  isTransitioning = false,
  onCreateTask,
  customTaskOperations,
  taskManagerOperations,
  selectedProjectId,
  onProjectFilterChange,
  allTasks,
  projectTaskCounts,
  editingTaskId,
  onCloseEdit
}) => {
  // Lift activeTask state up to be the single source of truth
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  
  // Use drag context to coordinate with MobilePageWrapper
  const { setIsDragging } = useDragContext();
  
  // Use custom hooks for state management and drag functionality
  const { days, isLoading, setShouldUseLocalTasks } = useTaskBoardState(initialTasks, isTransitioning);
  
  // Separate active and completed tasks for TaskManager drag hook
  const activeTasks = useMemo(() => 
    initialTasks?.filter(task => !task.isCompleted) || [], 
    [initialTasks]
  );
  const completedTasks = useMemo(() => 
    initialTasks?.filter(task => task.isCompleted) || [], 
    [initialTasks]
  );
  
  // Conditionally use TaskManager or standard drag hook
  const standardDragHook = useTaskBoardDrag(setActiveTask, setShouldUseLocalTasks);
  const taskManagerDragHook = useTaskManagerBoardDrag(
    setActiveTask, 
    setShouldUseLocalTasks,
    {
      activeTasks,
      completedTasks,
      taskManagerOperations: taskManagerOperations!
    }
  );
  
  // Choose drag hook based on whether TaskManager operations are provided
  const { handleDragStart, handleDragOver, handleDragEnd } = taskManagerOperations 
    ? taskManagerDragHook 
    : standardDragHook;
    
  const sensors = useTaskSensors();

  // Wrap drag handlers to update global drag state
  const wrappedHandleDragStart = useCallback((event: any) => {
    setIsDragging(true);
    handleDragStart(event);
  }, [setIsDragging, handleDragStart]);

  const wrappedHandleDragEnd = useCallback((event: any) => {
    setIsDragging(false);
    handleDragEnd(event);
  }, [setIsDragging, handleDragEnd]);

  // Show elegant skeleton when loading
  if (isLoading && days[0].tasks.length === 0) {
    return <TaskBoardLoading activeTaskCount={3} completedTaskCount={2} showCompletedExpanded={false} />;
  }

  return (
    <>
      <TaskAddHandler onAddTask={onAddTask} />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={wrappedHandleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={wrappedHandleDragEnd}
      >
        <div className="relative" style={{ overflow: 'visible' }}>
          <TaskBoardContent 
            days={days} 
            activeTask={activeTask} 
            setActiveTask={setActiveTask} 
            onCreateTask={onCreateTask}
            customTaskOperations={customTaskOperations}
            selectedProjectId={selectedProjectId}
            onProjectFilterChange={onProjectFilterChange}
            allTasks={allTasks}
            projectTaskCounts={projectTaskCounts}
          />
        </div>
      </DndContext>
    </>
  );
});

TaskBoard.displayName = 'TaskBoard';

export default TaskBoard;
